import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Modal, Button, Grid, Icon, Loader, Segment, Dimmer, Popup} from 'semantic-ui-react';
import {FilePond, registerPlugin} from 'react-filepond';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import {Actions} from '../../../common/actions';
import {maxSizeEachFileInMegaBytes, maxSizeAllFilesInMegaBytes} from '../../../common/constants/ftr';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css';
import 'filepond/dist/filepond.min.css';

registerPlugin(
  FilePondPluginFileValidateSize,
  FilePondPluginImagePreview,
);

const maxSizeEachFile = `${maxSizeEachFileInMegaBytes}MB`;
const maxSizeAllFiles = `${maxSizeAllFilesInMegaBytes}MB`;
const maxSizeEachFileInBytes = maxSizeEachFileInMegaBytes * 1000 * 1024;
const {setPartAttachments, fetchPartAttachments, savePartAttachments, editPartMasterInfo} = Actions;

const handleFileAction = (url, filename, action) => {
  if (action === 'download') {
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } else if (action === 'view') {
    window.open(url, '_blank');
  }
};

const getBase64Url = (base64, mimetype) => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], {'type': mimetype || 'application/pdf'});
  return window.URL.createObjectURL(blob);
};

const FilePreviewCard = ({filename, isPdf, isUserTde, onRemove, onDownload, onView}) => {
  return (
    <Segment raised>
      <Grid verticalAlign='middle'>
        <Grid.Row>
          <Grid.Column width={2} textAlign='center'>
            <Icon
              name={isPdf ? 'file pdf outline' : 'file outline'}
              size='large'
              color={isPdf ? 'red' : 'green'}
            />
          </Grid.Column>
          <Grid.Column width={6}>
            <strong>{filename}</strong>
          </Grid.Column>
          <Grid.Column width={8} textAlign='right'>
            <Button size='mini' basic color='teal' type='button' onClick={onView}>
              <Icon name='eye' /> Ver
            </Button>
            <Button size='mini' basic color='blue' type='button' onClick={onDownload}>
              <Icon name='download' /> Descargar
            </Button>
            {isUserTde && (
              <Button size='mini' basic color='red' type='button' onClick={onRemove}>
                <Icon name='trash alternate outline' /> Quitar
              </Button>
            )}
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Segment>
  );
};

const PartFiles = ({isUserTde, attachments, rawAttachments, dispatch, removedRaw, setRemovedRaw}) => {
  const onUpdateFiles = (fileItems) => {
    const files = fileItems.map((fileItem) => {
      return fileItem.file;
    });
    dispatch(setPartAttachments(files));
  };

  if (attachments && attachments.length > 0) {
    const file = attachments[0];
    const url = URL.createObjectURL(file);
    return (
      <FilePreviewCard
        filename={file.name}
        isPdf={false}
        isUserTde={isUserTde}
        onView={() => handleFileAction(url, file.name, 'view')}
        onDownload={() => handleFileAction(url, file.name, 'download')}
        onRemove={() => dispatch(setPartAttachments([]))}
      />
    );
  }

  if (!removedRaw && rawAttachments && rawAttachments.length > 0) {
    const fileData = rawAttachments[0];
    const url = getBase64Url(fileData.file, fileData.mimetype);
    return (
      <FilePreviewCard
        filename={fileData.name}
        isPdf={true}
        isUserTde={isUserTde}
        onView={() => handleFileAction(url, fileData.name, 'view')}
        onDownload={() => handleFileAction(url, fileData.name, 'download')}
        onRemove={() => {
          setRemovedRaw(true);
          dispatch(setPartAttachments([]));
        }}
      />
    );
  }

  if (isUserTde) {
    return (
      <FilePond
        allowMultiple={false}
        maxFiles={1}
        files={attachments}
        disabled={!isUserTde}
        credits={false}
        labelIdle='Arrastra tu archivo aquí o <span class="filepond--label-action">Navegador</span>'
        onupdatefiles={onUpdateFiles}
        maxFileSize={maxSizeEachFile}
        labelMaxFileSizeExceeded='Archivo demasiado grande'
        labelMaxFileSize={`El tamaño máximo de archivo es ${maxSizeEachFile}`}
        maxTotalFileSize={maxSizeAllFiles}
        labelMaxTotalFileSizeExceeded='Tamaño total máximo excedido'
        labelMaxTotalFileSize={`El tamaño máximo total es ${maxSizeAllFiles}`}
      />
    );
  }

  return <p style={{'textAlign': 'center', 'color': '#777'}}>No hay archivo adjunto</p>;
};

const AddPartFilesModal = ({
  partCode,
  wasSearched,
  wasSaved,
  isUserTde,
  attachments,
  rawAttachments,
  loadingAttachments,
}) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [removedRaw, setRemovedRaw] = useState(false);
  const dispatch = useDispatch();
  const formValues = useSelector((state) => state.partsMaster?.values || {});

  const closeModal = () => {
    setModalIsOpen(false);
    setRemovedRaw(false);
    dispatch(setPartAttachments([]));
  };

  const openModal = () => {
    setModalIsOpen(true);
    setRemovedRaw(false);
  };

  const fetchAttachments = () => {
    dispatch(fetchPartAttachments(partCode));
    openModal();
  };

  const hasNew = attachments && attachments.length > 0;
  const isModified = hasNew || removedRaw;

  const handleSaveFiles = () => {
    if (!isModified) {
      closeModal();
      return;
    }
    const formData = new FormData();
    attachments.forEach((element) => {
      if (element.size && element.size < maxSizeEachFileInBytes) {
        formData.append('file', element, element.name);
      }
    });

    dispatch(savePartAttachments(partCode, formData));
    const newHaveFilesStatus = hasNew ? 'S' : 'N';
    if (formValues.partCode) {
      const updatedValues = {
        ...formValues,
        'haveFiles': newHaveFilesStatus,
      };
      dispatch(editPartMasterInfo(partCode, updatedValues));
    }

    closeModal();
  };

  const renderContent = () => {
    if (loadingAttachments) {
      return (
        <Segment basic padded='very'>
          <Dimmer active inverted>
            <Loader inverted content='Cargando documento' />
          </Dimmer>
        </Segment>
      );
    }

    return (
      <PartFiles
        isUserTde={isUserTde}
        rawAttachments={rawAttachments}
        attachments={attachments}
        dispatch={dispatch}
        removedRaw={removedRaw}
        setRemovedRaw={setRemovedRaw}
      />
    );
  };

  const renderButton = () => {
    if (isUserTde && (wasSearched || wasSaved)) {
      return (
        <Button type='button' color='blue' onClick={fetchAttachments}>
          <Icon name='file'/>
          MSDS
        </Button>
      );
    }

    if (!isUserTde && wasSearched) {
      return (
        <Popup
          inverted
          position='top center'
          content='Descarga de archivos MSDS'
          trigger={
            <Icon
              name='download'
              color='red'
              size='big'
              onClick={fetchAttachments}
            />
          }
        />
      );
    }

    return null;
  };

  return (
    <Modal
      size='small'
      open={modalIsOpen}
      className='report-modal'
      trigger={renderButton()}
    >
      <Modal.Header>Documento MSDS</Modal.Header>
      <Modal.Content className='report-content'>
        {renderContent()}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={closeModal} type='button'>
          Cancelar
        </Button>
        {isUserTde && (
          <Button
            positive
            onClick={handleSaveFiles}
            type='button'
            disabled={!isModified}
          >
            <Icon name='save' /> Guardar
          </Button>
        )}
      </Modal.Actions>
    </Modal>
  );
};

export default AddPartFilesModal;
